import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface CardProps extends HTMLMotionProps<'div'> {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
  noPadding?: boolean;
  titleAction?: React.ReactNode;
  accent?: 'violet' | 'blue' | 'green' | 'red' | 'amber' | 'pink' | 'teal';
}

const accentMap = {
  violet: 'from-[#216974]/10 to-[#54c4b6]/10 border-[#54c4b6]/50',
  blue:   'from-blue-500/10 to-cyan-500/10 border-blue-200/50',
  green:  'from-emerald-500/10 to-teal-500/10 border-emerald-200/50',
  red:    'from-red-500/10 to-rose-500/10 border-red-200/50',
  amber:  'from-amber-500/10 to-orange-500/10 border-amber-200/50',
  pink:   'from-pink-500/10 to-rose-500/10 border-pink-200/50',
  teal:   'from-teal-500/10 to-cyan-500/10 border-teal-200/50',
};

const Card: React.FC<CardProps> = ({
  children,
  title,
  description,
  className = '',
  noPadding = false,
  titleAction,
  accent,
  ...props
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={`glass-card rounded-3xl overflow-hidden ${className}`}
      {...props}
    >
      {(title || description) && (
        <div
          className={`px-6 py-4 border-b border-white/40 flex items-center justify-between
            ${accent ? `bg-gradient-to-r ${accentMap[accent]}` : 'bg-gradient-to-r from-white/60 to-white/20'}`}
        >
          <div>
            {title && (
              <h3 className="text-sm font-semibold text-gray-700 tracking-tight">{title}</h3>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-gray-400 leading-relaxed">{description}</p>
            )}
          </div>
          {titleAction && <div>{titleAction}</div>}
        </div>
      )}
      <div className={noPadding ? '' : 'p-6'}>{children}</div>
    </motion.div>
  );
};

export default Card;
